import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import useHelpContextSignal from '../hooks/useHelpContextSignal';
import { scanBadge } from '../constants/scanStatus';
import { DOC_STATE, deriveDocState, estimateProgress, isUserJob } from '../utils/processingState';


// Decay schedule for upload-session polling after a trigger.
// Each consecutive idle poll advances one step; activity resets to step 0.
// After all steps are exhausted, polling continues at BASELINE_INTERVAL so
// the UI never goes completely stale — critical for cross-browser consistency.
const DECAY_STEPS = [3000, 3000, 3000, 5000, 5000, 10000, 15000, 30000];
const BASELINE_INTERVAL = 30000;

function fmtSize(b) {
  if (!b || b <= 0) return '—';
  if (b < 1024) return `${Math.round(b)} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
}

// Reason-code → short label for outcome_status='empty' docs. Server-side
// registry (backend/domain/extraction/content_reason_codes.py) is the
// source of truth — this is a UI fallback. Keep in sync with the same
// table in UploadHistory.jsx until the registry is shipped to the client.
const EMPTY_REASON_LABELS = {
  'transcription.empty.low_audio':       'Audio too quiet',
  'transcription.empty.no_speech':       'No speech detected',
  'transcription.empty.silence':         'Silent audio',
  'extraction.empty.image_only_pdf':     'Image-only PDF',
  'extraction.empty.blank_pages':        'Blank pages',
  'archive.empty.no_useful_files':       'Empty archive',
  'keyframes.empty.no_visual_change':    'Static video',
  'keyframes.empty.black_frames':        'Black-frame video',
};
function emptyLabel(reasonCode) {
  return EMPTY_REASON_LABELS[reasonCode] || 'No content';
}
// A "successfully processed but empty" doc is NOT a failure — the pipeline
// ran cleanly, just produced no extractable content. Treat as its own
// third state alongside completed/failed so the UI doesn't mislead the
// user into thinking the system broke.
function isEmptyOutcome(doc) {
  return doc.status === 'completed' && doc.outcome_status === 'empty';
}

function ProgressRow({ filename, meta, badgeText, badgeCls, pct, fillCls }) {
  return (
    <div className="am-row">
      <div className="am-row-header">
        <span className="am-filename">{filename}</span>
        <span className="am-meta">{meta}</span>
        <span className={`am-badge ${badgeCls}`}>{badgeText}</span>
      </div>
      <div className="am-progress">
        <div className={`am-progress-fill ${fillCls}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function TransferRow({ u, fmtSize, onCancel }) {
  const [logOpen, setLogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const logRef = useRef(null);
  const total = u.total_bytes || 1;
  const uploaded = u.bytes_uploaded || 0;
  const pct = Math.min(Math.round(uploaded / total * 100), 100);
  // "stale" is a presentation hint derived from is_stale on read — when fresh
  // heartbeats resume the row self-heals back to plain "uploading". A row is
  // only finished if the underlying status is terminal. The legacy "stale"
  // status (sticky pre-fix rows) is still rendered as finished.
  const isStale = !!u.is_stale && u.status === 'uploading';
  const isLegacyStale = u.status === 'stale';
  const isFinished = u.status === 'completed' || u.status === 'failed'
    || isLegacyStale || u.status === 'cancelled';
  let meta = `${fmtSize(uploaded)} / ${fmtSize(total)} · ${pct}%`;
  if (!isFinished && u.transfer_speed_bps > 0 && !isStale) {
    meta += ` · ${fmtSize(u.transfer_speed_bps)}/s`;
    const remaining = total - uploaded;
    if (remaining > 0) {
      const eta = Math.round(remaining / u.transfer_speed_bps);
      meta += eta < 60 ? ` · ETA ${eta}s` : ` · ETA ${Math.floor(eta / 60)}m ${eta % 60}s`;
    }
  }
  if (isStale && typeof u.silent_seconds === 'number') {
    const s = u.silent_seconds;
    meta += ` · no heartbeat ${s < 120 ? `${s}s` : `${Math.floor(s / 60)}m`}`;
  }
  if (isFinished && u.completed_at) {
    const ago = Math.round((Date.now() - new Date(u.completed_at).getTime()) / 1000);
    meta = `${fmtSize(uploaded)} / ${fmtSize(total)}` +
      (ago < 60 ? ` · ${ago}s ago` : ` · ${Math.floor(ago / 60)}m ago`);
  }
  const entries = u.log_entries || [];

  const badgeText = u.status === 'completed' ? 'Done'
    : u.status === 'failed' ? 'Failed'
    : isLegacyStale ? 'Stale'
    : u.status === 'cancelled' ? 'Cancelled'
    : isStale ? 'No heartbeat'
    : 'Uploading';
  const badgeCls = u.status === 'completed' ? 'am-badge-done'
    : u.status === 'failed' ? 'am-badge-failed'
    : isLegacyStale ? 'am-badge-failed'
    : u.status === 'cancelled' ? 'am-badge-failed'
    : isStale ? 'am-badge-failed'  // amber-ish; reuses failed styling for now
    : 'am-badge-uploading';

  async function handleCancel() {
    if (cancelling || !u.id) return;
    setCancelling(true);
    try {
      await onCancel(u.id);
    } finally {
      // Leave cancelling=true on success — the next poll will set isFinished
      // and unmount this control. Re-enable on error so the user can retry.
      setCancelling(false);
    }
  }

  // Auto-scroll log pane to bottom when new entries arrive
  useEffect(() => {
    if (logOpen && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logOpen, entries.length]);

  return (
    <div className={`am-row ${isFinished ? 'am-row-finished' : ''}`}>
      <div className="am-row-header">
        <span className="am-filename">{u.filename}</span>
        <span className="am-meta">{meta}</span>
        <span className={`am-badge ${badgeCls}`}>{badgeText}</span>
        {!isFinished && onCancel && (
          <button
            type="button"
            className="am-cancel-btn"
            disabled={cancelling}
            title={cancelling ? 'Cancelling…' : 'Cancel this upload'}
            onClick={handleCancel}
            style={{
              background: 'transparent',
              border: '1px solid #d32f2f',
              color: '#d32f2f',
              borderRadius: 4,
              padding: '1px 8px',
              fontSize: 11,
              cursor: cancelling ? 'wait' : 'pointer',
              marginLeft: 6,
            }}
          >
            {cancelling ? 'Cancelling…' : '✕ Cancel'}
          </button>
        )}
        {entries.length > 0 && (
          <span className="am-log-toggle" onClick={() => setLogOpen(o => !o)}>
            {logOpen ? '▲ hide log' : `▼ log (${entries.length})`}
          </span>
        )}
      </div>
      {!isFinished && (
        <div className="am-progress">
          <div className="am-progress-fill am-fill-upload" style={{ width: `${pct}%` }} />
        </div>
      )}
      {isFinished && u.error_message && (
        <div className="am-error-detail">{u.error_message}</div>
      )}
      {logOpen && entries.length > 0 && (
        <div className="am-log-pane" ref={logRef}>
          {entries.map((e, i) => (
            <div key={i} className={`am-log-line ${e.level === 'ok' ? 'ok' : e.level === 'err' ? 'err' : e.level === 'warn' ? 'warn' : ''}`}>
              [{e.ts}] {e.msg}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CollapsibleGroup({ icon, label, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  if (count === 0) return null;
  return (
    <div className="am-group">
      <div className="am-group-header" onClick={() => setOpen(o => !o)}>
        <span className={`am-caret ${open ? 'open' : ''}`}>▶</span>
        <span className="am-icon">{icon}</span>
        <span className="am-label">{label}</span>
        <span className="am-count">{count}</span>
      </div>
      {open && <div className="am-group-body">{children}</div>}
    </div>
  );
}

// Triggered-decay polling hook.
// Returns a `trigger()` function. Call it to (re)start polling from the fast end.
// Also re-triggers on tab visibility restore.
// `callback` must return a boolean: true = activity found, keep fast; false = advance decay.
// IMPORTANT: polling never fully stops — after decay steps exhaust, it continues
// at BASELINE_INTERVAL so cross-browser/session state stays consistent.
function useDecayPolling(callback) {
  const timerRef = useRef(null);
  const stepRef = useRef(0); // start at step 0 (fast)
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const scheduleRef = useRef(null);

  useEffect(() => {
    scheduleRef.current = function scheduleNext() {
      if (timerRef.current) clearTimeout(timerRef.current);
      const step = stepRef.current;
      // After decay steps exhaust, keep polling at baseline interval
      const interval = step < DECAY_STEPS.length ? DECAY_STEPS[step] : BASELINE_INTERVAL;

      timerRef.current = setTimeout(async () => {
        timerRef.current = null;
        let active = false;
        try { active = await callbackRef.current(); } catch { /* non-fatal */ }
        if (active) {
          stepRef.current = 0; // reset to fast
        } else if (step < DECAY_STEPS.length) {
          stepRef.current = step + 1; // advance decay
        }
        // else: stay at baseline
        scheduleRef.current?.();
      }, interval);
    };

    const trigger = () => {
      stepRef.current = 0;
      // Immediate fetch then schedule
      (async () => {
        try { await callbackRef.current(); } catch { /* non-fatal */ }
        scheduleRef.current?.();
      })();
    };

    // Immediate check on mount
    trigger();

    const onVisible = () => {
      if (document.visibilityState === 'visible') trigger();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return useCallback(() => {
    stepRef.current = 0;
    scheduleRef.current?.();
  }, []);
}

// The oldest uploader that can keep the handed-off token alive. A Firebase
// token needs 1.0.11 (env-aware config). The backend's own session token
// (`provider: google_identity`, no refresh token) needs 1.0.15, which renews
// it at POST /api/v1/auth/refresh — an older uploader works for the token's
// first hour and then stalls without a message.
function minUploaderVersion(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (payload.provider === 'google_identity') return '1.0.15';
  } catch { /* not a JWT (dev mode) */ }
  return '1.0.11';
}

// Build the deep link URL for the Desktop Uploader.
//
// Carries `backend_url` so the uploader fetches the rest of its env config
// (Firebase project ID, public API key, update feed) from
// {backend_url}/api/v1/config/uploader instead of bundling env-specific
// values into the binary. Single uploader binary serves dev / staging /
// prod / future client deployments.
function buildUploaderDeepLink(token) {
  const user = JSON.parse(localStorage.getItem('dc_user') || '{}');
  const refreshToken = localStorage.getItem('dc_refresh_token') || '';
  const params = new URLSearchParams({
    token: token,
    refresh_token: refreshToken,
    email: user.email || '',
    uid: user.uid || '',
    name: user.name || user.email || '',
    backend_url: window.location.origin,
    min_version: minUploaderVersion(token),
  });
  return `chargestack-upload://auth?${params.toString()}`;
}

// Try to launch the Desktop Uploader via custom protocol.
// On Windows, the OS shows a confirmation dialog which triggers blur even if
// the app exe is missing. We use a longer timeout and require the page to
// stay hidden (app actually took foreground) to count as "opened".
function tryLaunchUploader(token, { onOpened, onNotInstalled }) {
  if (!token) return;

  const deepLink = buildUploaderDeepLink(token);
  let didBlur = false;
  let blurTime = 0;

  const onBlur = () => { didBlur = true; blurTime = Date.now(); };
  window.addEventListener('blur', onBlur);

  // Fire the deep link via hidden iframe
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = deepLink;
  document.body.appendChild(iframe);
  setTimeout(() => iframe.remove(), 500);

  // After 3.5s, check if the app actually took foreground.
  // The Windows protocol confirmation dialog will blur+refocus quickly,
  // but a real app launch keeps the page hidden for longer.
  setTimeout(() => {
    window.removeEventListener('blur', onBlur);
    const pageHidden = document.hidden || document.visibilityState === 'hidden';
    const blurredLongEnough = didBlur && (Date.now() - blurTime) > 1500;
    if (pageHidden || blurredLongEnough) {
      onOpened();
    } else {
      onNotInstalled();
    }
  }, 3500);
}

const DOWNLOAD_URLS = {
  mac: '/api/downloads/mac',
  win: '/api/downloads/win',
};

function detectOS() {
  const ua = navigator.userAgent || '';
  if (ua.includes('Mac')) return 'mac';
  if (ua.includes('Win')) return 'win';
  return 'unknown';
}

// Props:
//   caseId          — current case UUID
//   documents       — document list from UploadTab context (for processing/indexing rows)
//   costLimitUsd    — optional cost cap passed to GcsUploader
//   onUploadComplete — called when an uploader reports completion (triggers doc refresh)
//   onUploadStart   — called when an upload begins (triggers doc polling in parent)
export default function ActivityMonitor({ caseId, documents, costLimitUsd, onUploadComplete, onUploadStart }) {
  const { token, logout } = useAuth();
  const [panelOpen, setPanelOpen] = useState(true);
  const [uploaderOpen, setUploaderOpen] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const prevUploadSigRef = useRef('');
  const pollRef = useRef(null);
  // 'idle' | 'launching' | 'downloading' | 'waiting_install' | 'opened'
  const [desktopState, setDesktopState] = useState('idle');

  // Clean up polling on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startInstallPoll = (t) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      tryLaunchUploader(t, {
        onOpened: () => { clearInterval(pollRef.current); setDesktopState('opened'); },
        onNotInstalled: () => {},
      });
    }, 8000); // 8s: longer than the 2.5s detection timeout
    // Give up after 15 minutes
    setTimeout(() => { if (pollRef.current) clearInterval(pollRef.current); }, 900000);
  };

  const handleDesktopUploader = () => {
    if (!token) {
      alert('Please sign in to Discovery Check first, then try again.');
      return;
    }
    if (pollRef.current) clearInterval(pollRef.current);
    setDesktopState('launching');
    tryLaunchUploader(token, {
      onOpened: () => setDesktopState('opened'),
      onNotInstalled: () => {
        // Auto-download the right installer for this OS
        const os = detectOS();
        const url = os === 'win' ? DOWNLOAD_URLS.win : DOWNLOAD_URLS.mac;
        setDesktopState('downloading');
        const a = document.createElement('a');
        a.href = url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Start polling — page will auto-connect when they open the app
        setTimeout(() => {
          setDesktopState('waiting_install');
          startInstallPoll(token);
        }, 1000);
      },
    });
  };

  // Fetch upload sessions; returns true if uploads are active or something changed.
  const fetchUploads = useCallback(async () => {
    if (!token) return false; // auth not restored yet — an unauthenticated 401 is not "token rejected"
    try {
      const headers = {};
      if (token && token !== 'dev-mode') headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(
        `/api/v1/documents/upload-progress/case/${encodeURIComponent(caseId)}`,
        { headers }
      );
      if (res.status === 401 || res.status === 403) {
        // Token rejected — kick to Portal sign-in. AuthContext.logout('expired')
        // stashes the reason and route so we land back on this case page once
        // the user signs back in (whether they go through the Portal or not).
        try { logout && logout('expired'); } catch {}
        return false;
      }
      if (!res.ok) return false;
      const data = await res.json();
      const list = data.uploads || [];
      // Include is_stale + silent_seconds in the change signature so a row
      // flipping is_stale (after heartbeat resumes) re-renders even if bytes
      // are otherwise unchanged in this tick.
      const sig = list.map(u =>
        `${u.id}:${u.bytes_uploaded}:${u.status}:${u.is_stale ? 1 : 0}`
      ).join(',');
      const changed = sig !== prevUploadSigRef.current;
      prevUploadSigRef.current = sig;
      setUploads(list);
      if (changed) setLastUpdate(new Date());
      // Signal "active" if any uploads are still in-progress (even if stale —
      // we want to keep polling so we can detect heartbeat resumption).
      const hasActive = list.some(u => u.status === 'uploading');
      return hasActive || changed;
    } catch {
      return false;
    }
  }, [caseId, token, logout]);

  const triggerPolling = useDecayPolling(fetchUploads);

  // Per-session cancel — POSTs to the cancel endpoint, then nudges the poll
  // so the user sees the row flip to 'Cancelled' within ~1s without having
  // to wait for the next decay-polling tick.
  const handleCancelTransfer = useCallback(async (sessionId) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token && token !== 'dev-mode') headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(
        `/api/v1/documents/upload-progress/${encodeURIComponent(sessionId)}/cancel`,
        { method: 'POST', headers }
      );
      if (!res.ok && res.status !== 409) {
        // 409 = already terminal, treat as success.
        const detail = await res.text().catch(() => '');
        console.warn('Cancel failed:', res.status, detail);
      }
    } catch (err) {
      console.warn('Cancel request errored:', err);
    } finally {
      // Refresh immediately so the UI reflects the cancel.
      fetchUploads();
    }
  }, [token, fetchUploads]);

  const handleUploadStart = useCallback(() => {
    triggerPolling();
    onUploadStart?.();
  }, [triggerPolling, onUploadStart]);

  // Active background jobs (complete_extraction, stages_backfill,
  // transcription, etc.). Polled separately from uploads because the
  // upload-progress endpoint doesn't cover post-ingest work. Active AUTO
  // jobs' document_ids drive the calm case-level "Finalizing case analysis"
  // line below; user-initiated jobs are surfaced by BackgroundJobsBanner.
  // Seed from the case-page mount dashboard envelope so the first mount
  // doesn't fan out another /background-jobs request — BackgroundJobsBanner
  // is already seeded the same way, and the dashboard endpoint returned
  // this data inside one backend session. Pool Governance P2.
  const { caseDashboard } = useApp();
  const [bgJobs, setBgJobs] = useState(() => caseDashboard?.background_jobs?.jobs || []);
  const bgJobsActiveRef = useRef(false);
  bgJobsActiveRef.current = bgJobs.some(j => j.status === 'queued' || j.status === 'processing');
  // Read the token at FETCH time, not effect-mount time: this effect's deps
  // deliberately exclude `token` (a token refresh must not reset the poll
  // timer), so a mount-time closure kept polling with a dead token after
  // re-login — and that stale 401 fed the cross-tab logout race.
  const bgTokenRef = useRef(token);
  bgTokenRef.current = token;
  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    let timer = null;
    // Adaptive cadence + visibility gate. 5s while jobs are actively
    // running; back off to 20s once they all settle. Skip entirely
    // when the tab is hidden. BackgroundJobsBanner polls the same
    // endpoint independently, so let's at least make this one cheap.
    const fetchJobs = async () => {
      if (document.visibilityState !== 'visible') { schedule(); return; }
      try {
        const t = bgTokenRef.current;
        const headers = {};
        if (t && t !== 'dev-mode') headers['Authorization'] = `Bearer ${t}`;
        const res = await fetch(
          `/api/v1/exploration/case/${encodeURIComponent(caseId)}/background-jobs`,
          { headers }
        );
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setBgJobs(data.jobs || []);
        }
      } catch { /* non-fatal */ }
      schedule();
    };
    const schedule = () => {
      if (cancelled) return;
      const next = bgJobsActiveRef.current ? 5000 : 20000;
      timer = setTimeout(fetchJobs, next);
    };
    // Skip the immediate fetch when seeded from the dashboard envelope —
    // jump straight to the polling cadence. First refresh is bounded by
    // the active/idle interval, which is fine since BackgroundJobsBanner
    // covers the live case.
    if (caseDashboard?.background_jobs?.jobs) {
      schedule();
    } else {
      fetchJobs();
    }
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, token]);

  // Distinct docs in active AUTO background jobs — post-completion enrichment
  // (vector indexing + graph extraction) that runs across the case AFTER a
  // document's own pipeline finishes. Drives the calm case-level "Finalizing
  // case analysis" line; never re-flips a finished doc back to Processing.
  // User-initiated jobs (Deep search, transcription) are surfaced separately
  // by BackgroundJobsBanner, not here.
  const enrichingDocIds = useMemo(() => {
    const ids = new Set();
    for (const j of bgJobs) {
      if (j.status !== 'queued' && j.status !== 'processing') continue;
      if (isUserJob(j)) continue;
      const list = (j.params?.document_ids) || (j.params?.document_id ? [j.params.document_id] : []);
      for (const id of list) ids.add(id);
    }
    return ids;
  }, [bgJobs]);

  // Processing rows — one collapsed bucket for every document still moving
  // through its OWN pipeline (triage, extraction, analysis). A single
  // "Processing" state with a coarse bar; internal stage names are not
  // surfaced. deriveDocState is the single seam (and where a backend-computed
  // processing_state would later plug in). Mutually exclusive with the Done
  // bucket by construction — a doc has exactly one state — so a finished doc
  // can never render in both. Post-completion enrichment does NOT belong here;
  // it's the case-level line. Replaces the former Processing / Queued /
  // Indexing / Upgrading sections; the queue backlog stays visible via the count.
  const processingRows = useMemo(() => {
    const out = [];
    for (const doc of (documents || [])) {
      if (deriveDocState(doc) !== DOC_STATE.PROCESSING) continue;
      out.push({
        id: doc.id,
        filename: doc.filename || 'Unknown',
        fileSize: fmtSize(doc.file_size),
        pct: estimateProgress(doc),
      });
    }
    return out;
  }, [documents]);

  // Recently finished — completed or failed in the last 5 minutes
  const RECENT_WINDOW_MS = 5 * 60 * 1000;
  const recentDocs = useMemo(() =>
    (documents || [])
      .filter(d => {
        if (d.status !== 'completed' && d.status !== 'failed') return false;
        if (!d.updated_at) return false;
        const age = Date.now() - new Date(d.updated_at).getTime();
        return age < RECENT_WINDOW_MS && age >= 0;
      })
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)),
  [documents]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeUploads = uploads.filter(u => u.status === 'uploading');
  const enrichingCount = enrichingDocIds.size;
  const activeCount = activeUploads.length + processingRows.length;
  // Help-context signal: how many items are in flight (transferring or
  // processing). Count only — never filenames.
  useHelpContextSignal('uploads_in_progress', activeCount);
  const subtitleParts = [];
  if (activeUploads.length > 0) subtitleParts.push(`${activeUploads.length} uploading`);
  if (processingRows.length > 0) subtitleParts.push(`${processingRows.length} processing`);
  if (recentDocs.length > 0) subtitleParts.push(`${recentDocs.length} done`);
  if (enrichingCount > 0) subtitleParts.push(`finalizing ${enrichingCount}`);

  const timeStr = lastUpdate
    ? lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '';

  return (
    <div className="am-panel">
      <div className="am-panel-header" onClick={() => setPanelOpen(o => !o)}>
        <span className={`am-caret ${panelOpen ? 'open' : ''}`}>▶</span>
        <span className="am-title">📋 Activity Monitor</span>
        <span className="am-subtitle">{subtitleParts.join(', ')}</span>
      </div>

      {panelOpen && (
        <div className="am-panel-body">

          {/* ── Upload Files section ── */}
          <div className="am-uploader-section">
            <div className="am-uploader-header" onClick={() => setUploaderOpen(o => !o)}>
              <span className={`am-caret ${uploaderOpen ? 'open' : ''}`}>▶</span>
              <span className="am-icon">📤</span>
              <span className="am-label">Upload Files</span>
            </div>
            {uploaderOpen && (
              <div className="am-uploader-body" style={{ textAlign: 'center', padding: '32px 16px' }}>
                <button
                  className="btn btn-primary"
                  style={{ fontSize: 15, padding: '10px 28px' }}
                  onClick={handleDesktopUploader}
                  disabled={desktopState === 'launching' || desktopState === 'downloading' || desktopState === 'waiting_install'}
                >
                  {desktopState === 'launching' ? '⏳ Checking...' : '🖥️ Open Desktop Uploader'}
                </button>

                {desktopState === 'opened' && (
                  <p style={{ color: '#28a745', fontSize: 13, marginTop: 12 }}>
                    ✅ Chargestack Uploader opened — switch to the app to start uploading.
                  </p>
                )}

                {desktopState === 'downloading' && (
                  <p style={{ color: '#2563EB', fontSize: 13, marginTop: 12 }}>
                    ⬇️ Downloading Chargestack Uploader...
                  </p>
                )}

                {desktopState === 'waiting_install' && (
                  <div style={{ marginTop: 14, padding: '16px', background: '#f0f7ff', borderRadius: 8, border: '1px solid #b3d4ff' }}>
                    <p style={{ color: '#333', fontSize: 13, margin: '0 0 6px 0' }}>
                      <strong>⏳ The installer is in your Downloads folder</strong>
                    </p>
                    <p style={{ color: '#555', fontSize: 12, margin: '0 0 10px 0' }}>
                      We just downloaded the Chargestack Uploader installer. Open it from your
                      browser&apos;s downloads (or your Downloads folder) and run it — this page
                      connects automatically once the app is installed.
                    </p>
                    <p style={{ color: '#555', fontSize: 12, margin: '0 0 10px 0' }}>
                      Didn&apos;t get it? Download again for{' '}
                      <a href={DOWNLOAD_URLS.mac}>Mac</a> or <a href={DOWNLOAD_URLS.win}>Windows</a>.
                    </p>
                    <button
                      className="btn btn-outline-secondary"
                      style={{ fontSize: 12, padding: '4px 14px' }}
                      onClick={handleDesktopUploader}
                    >Already installed? Try again</button>
                  </div>
                )}

                {desktopState === 'idle' && (
                  <div style={{ color: '#888', fontSize: 12, marginTop: 10, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
                    <p style={{ margin: '0 0 4px 0' }}>
                      Uploads run through the free Chargestack Desktop Uploader — it handles whole
                      folders and large evidence files, and resumes if your connection drops.
                    </p>
                    <p style={{ margin: 0 }}>
                      If it isn&apos;t installed yet, clicking above downloads the installer
                      (<a href={DOWNLOAD_URLS.mac}>Mac</a> · <a href={DOWNLOAD_URLS.win}>Windows</a>).
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Uploading (active + recent transfers from the upload-session
                 poll). Finished transfer rows carry their own Done/Failed
                 badge and self-prune server-side. ── */}
          <CollapsibleGroup icon="📤" label="Uploading" count={uploads.length}>
            {uploads.map(u => (
              <TransferRow key={u.id || u.filename} u={u} fmtSize={fmtSize} onCancel={handleCancelTransfer} />
            ))}
          </CollapsibleGroup>

          {/* ── Processing — single collapsed bucket for everything between
                 "uploaded" and "ready to search": triage, extraction, analysis,
                 indexing, and auto enrichment. No per-stage labels leak here;
                 the user sees one "Processing" state. Replaces the former
                 Processing / Queued / Indexing / Upgrading sections. ── */}
          <CollapsibleGroup icon="🔄" label="Processing" count={processingRows.length}>
            {processingRows.map(r => (
              <ProgressRow
                key={r.id}
                filename={r.filename}
                meta={r.fileSize}
                badgeText="Processing"
                badgeCls="am-badge-analyzing"
                pct={r.pct}
                fillCls="am-fill-process"
              />
            ))}
          </CollapsibleGroup>

          {/* ── Done ──
              Terminal states, distinct badges:
                - Done:        status=completed, has extractable content
                - No content:  status=completed, outcome_status='empty'
                               (pipeline succeeded, just nothing to extract —
                               NOT a failure, no red row tint)
                - Failed:      status=failed (system genuinely couldn't process)
                - Malware:     quarantined by the scan gate */}
          <CollapsibleGroup icon="✅" label="Done" count={recentDocs.length} defaultOpen={false}>
            {recentDocs.map(d => {
              const ago = Math.round((Date.now() - new Date(d.updated_at).getTime()) / 1000);
              const agoStr = ago < 60 ? `${ago}s ago` : `${Math.floor(ago / 60)}m ago`;
              const scan = scanBadge(d);
              const empty = isEmptyOutcome(d);
              const isFailed = d.status === 'failed';
              let badgeCls, badgeText, rowCls = '', badgeTitle;
              if (scan) {
                // Quarantined-as-malware reads as a block, not a generic failure.
                badgeCls = scan.amCls;
                badgeText = scan.longLabel;
                badgeTitle = scan.title;
                if (scan.state === 'infected') rowCls = 'am-row-failed';
              } else if (empty) {
                badgeCls = 'am-badge-empty';
                badgeText = emptyLabel(d.outcome_reason_code);
                badgeTitle = d.outcome_reason_code || 'No content extracted';
              } else if (isFailed) {
                badgeCls = 'am-badge-failed';
                badgeText = 'Failed';
                rowCls = 'am-row-failed';
              } else {
                badgeCls = 'am-badge-done';
                badgeText = 'Done';
              }
              return (
                <div key={d.id} className={`am-row ${rowCls}`}>
                  <div className="am-row-header">
                    <span className="am-filename">{d.filename || 'Unknown'}</span>
                    <span className="am-meta">{agoStr}</span>
                    <span
                      className={`am-badge ${badgeCls}`}
                      title={badgeTitle}
                    >
                      {badgeText}
                    </span>
                  </div>
                  {isFailed && d.processing_error && (
                    <div className="am-error-detail">{d.processing_error}</div>
                  )}
                </div>
              );
            })}
          </CollapsibleGroup>

          {/* ── Case-level enrichment (honest, correctly-scoped) ──
                 A document is "Done" once its OWN pipeline finishes. Vector
                 indexing + graph extraction then run asynchronously across the
                 case to finish search and cross-doc analysis. Surfaced once,
                 calmly, here — instead of re-flipping finished docs back to
                 Processing. Disappears when the enrichment work settles. */}
          {enrichingCount > 0 && (
            <div style={{
              padding: '8px 14px', fontSize: 12, color: '#6b7280',
              borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%', background: '#9ca3af',
                display: 'inline-block', flex: '0 0 auto',
              }} />
              Finalizing case analysis · {enrichingCount} document{enrichingCount === 1 ? '' : 's'}
            </div>
          )}

          {/* ── Empty state ── */}
          {activeCount === 0 && recentDocs.length === 0 && enrichingCount === 0 && (
            <div className="am-empty">
              ✅ All caught up — no active uploads or processing.
            </div>
          )}

          {timeStr && <div className="am-footer">Last updated {timeStr}</div>}
        </div>
      )}
    </div>
  );
}
